import { hygraphClient } from '@/lib/graphql-clients';
import {
  FaqListing,
  FaqPage as HygraphFaqPage,
  FaqPageWhereInput,
  getSdk,
  Page,
  RowrowComponentsUnion,
} from '@/lib/types';

import faqPageCardTransformer from '@/features/faq-page/hygraph/transformers/faq-page-card.transformer';
import {
  FaqPage,
  FaqPageCard,
  FaqPageListingMap,
} from '@/features/faq-page/type';
import { getPageComponents } from '@/utils/page-static-utils';

const getSingle = async (
  id: string,
  category?: string,
  specialty?: string,
  discipline?: string,
  state?: string
): Promise<FaqPageCard[]> => {
  const sdk = getSdk(hygraphClient(false));

  const filters: FaqPageWhereInput | null = {
    id_not: id,
  };

  if (category) {
    filters.categories_some = {
      categorySlug: category,
    };
  }

  if (specialty) {
    filters.specialty = {
      slug: specialty,
    };
  }

  if (discipline) {
    filters.discipline = {
      slug: discipline,
    };
  }

  if (state) {
    filters.state = {
      slug: state,
    };
  }
  const { faqPages } = await sdk.FaqPages({
    filters,
  });

  return faqPages.map((faqPage) =>
    faqPageCardTransformer(faqPage as HygraphFaqPage)
  );
};

const get = async ({
  id,
  mainContent: components,
}: FaqPage): Promise<FaqPageListingMap> => {
  const usefulComponents = components.filter(
    (c) => c.__typename === 'FaqListing'
  ) as FaqListing[];

  if (!usefulComponents.length) return {};

  return Object.fromEntries(
    await Promise.all(
      usefulComponents.map(async (c) => {
        const cards = await getSingle(
          id,
          c.category?.categorySlug || undefined,
          c.specialty?.slug,
          c.discipline?.slug,
          c.state?.slug
        );

        return [c.id, cards];
      })
    )
  );
};

const getByPage = async (page: Page): Promise<FaqPageListingMap> => {
  const check = (rc: RowrowComponentsUnion) => rc.__typename === 'FaqListing';
  const pageComponents = getPageComponents(page, check);
  if (pageComponents.length === 0) {
    return {};
  }

  // component should be FaqListing
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const faqPageListingRequests = pageComponents.map(async (component: any) => {
    const cards = await getSingle(
      component.id,
      component.category?.categorySlug || undefined,
      component.specialty?.slug,
      component.discipline?.slug,
      component.state?.slug
    );
    return [component.id, cards];
  });

  return Object.fromEntries(await Promise.all(faqPageListingRequests));
};

const FaqPageListingService = {
  get,
  getByPage,
};

export default FaqPageListingService;
